import { userService } from '../services/user.service';
import Vue from 'vue';
import axios from 'axios';
import { authHeader } from '@/helpers';
import eventbus from '@/eventbus';
import _ from 'lodash';
import * as Sentry from '@sentry/vue';

const state = {
  user: {
    infos: null,
    token: ''
  },
  status: {}
};

const actions = {

  /**
   * Login utilisateur
   * @param dispatch
   * @param commit
   * @param email - Email to use for login
   * @param password - Password to use for login
   * @returns {Promise<any>}
   */
  async login({ dispatch, commit }, { email, password }) {
    try {
      commit('loginRequest', { username: email });
      const user = await userService.login(email, password);
      commit('loginSuccess', user);
      Sentry.setUser({ email: email });
    }
    catch (error) {
      commit('loginFailure', error);
      let errorMessage = error;
      switch (error) {
        case 'Your account email is not confirmed.':
          errorMessage = 'Votre adresse courriel n\'pas encore été confirmée, un courriel vous a été envoyé pour ceci';
          break;
        case 'Your account has been blocked by the administrator.':
          errorMessage = 'Votre compte n\'a pas encore été validé par l\'administrateur car votre paiement n\'a pas encore été reçu';
          break;
        case 'Identifier or password invalid.':
          errorMessage = 'Identifiant ou mot de passe invalide';
          break;
      }
      throw errorMessage;
    }
  },

  /**
   * Logout utilisateur
   * @param commit
   */
  logout({ commit }) {
    localStorage.removeItem('vuex');
    commit('logout');
    Sentry.setUser(null);
  },

  /**
   * Create new user.
   *
   * @param dispatch
   * @param commit
   * @param data
   *
   * @returns {Promise<any>}
   */
  async register({ dispatch, commit }, data) {
    const user = {
      // username: data.user.email,
      email: data.user.email,
      password: data.user.password,
      // role: 'customer',
      name: `${data.member.firstName} ${data.member.lastName}`,
      address: {
        __component: 'member.address',
        street: data.user.address.street,
        app: data.user.address.app,
        city: data.user.address.city,
        // eslint-disable-next-line camelcase
        postal_code: data.user.address.postal_code,
        province: data.user.address.province,
        country: data.user.address.country
      },
      isFrom: data.user.isFrom,
      isFromOther: data.user.isFromOther
    };

    const member = {
      firstName: data.member.firstName,
      lastName: data.member.lastName,
      birthday: data.member.birthday,
      email: data.user.email,
      formation: data.member.formation,
      occupation: data.member.occupation,
      phonePrincipal: data.member.phonePrincipal,
      phoneMobile: data.member.phoneMobile,
      newsletter: data.member.newsletter,
      commitees: {
        __component: 'member.commitees',
        'Bulletin_textes': data.member.commitees.indexOf('Bulletin_textes') >= 0,
        'Bulletin_envoi': data.member.commitees.indexOf('Bulletin_envoi') >= 0,
        'Activites_excursions': data.member.commitees.indexOf('Activites_excursions') >= 0,
        'Kiosques_salons': data.member.commitees.indexOf('Kiosques_salons') >= 0,
        'CA': data.member.commitees.indexOf('CA') >= 0,
        'Compilations_observations': data.member.commitees.indexOf('Compilations_observations') >= 0,
        'Recherche_commanditaires': data.member.commitees.indexOf('Recherche_commanditaires') >= 0
      }
    };

    // Send the request to the backend
    try {
      const response = await axios.post('/coq/register', { user, member }, {
        headers: { ...authHeader() }
      });
      const createdUser = response.data;
      commit('registerSuccess', createdUser);
      return createdUser;
    }
    catch (e) {
      if (e.response.status === 401) {
        eventbus.$emit('logout');
      }
      throw e.response.data.message;
    }
  },

  /**
   * Mettre à jour les informations utilisateurs
   * @param dispatch
   * @param commit
   * @param data
   * @returns {Promise<void>}
   */
  async updateInfos({ state, commit }, data) {
    if (_.isUndefined(data)) {
      commit('updateAccountFailure', 'Informations manquantes');
      return;
    }
    if (!_.isObject(data) || _.isEmpty(data)) {
      commit('updateAccountFailure', 'Informations invalides');
      return;
    }
    if (!_.has(state.user, 'infos')) {
      commit('updateAccountFailure', 'Utilisateur non connecté');
      return;
    }
    if (!_.has(state.user.infos, '_id')) {
      commit('updateAccountFailure', 'Id utilisateur introuvable');
      return;
    }

    commit('updateAccountRequest');

    data['_id'] = state.user.infos._id;

    const update = await userService.update(data);
    commit('updateAccountSuccess', update);

    return update;
  },

  async getInvoices() {
    return userService.invoices();
  },

  /**
   * Modification du mot de passe
   * @param commit
   * @param data
   * @returns {Promise<any>}
   */
  async changePassword({ commit }, data) {
    return userService.changePassword(data);
  },

  /**
   * Mot de passe oublié.
   * @param commit
   * @param email
   */
  async forgotPassword({ commit }, email) {
    return userService.forgotPassword(email);
  },

  /**
   * Reinitialisation du mot de passe
   * @param commit
   * @param data
   * @returns {Promise<any>}
   */
  async resetPassword({ commit }, data) {
    return userService.resetPassword(data);
  },

  async subscribeToPlan({ commit, state }, plan) {
    const user = userService.subscribeToPlan(plan);
    commit('refresh', user);
    return user;
  },

  async refresh({ commit, state }) {
    const user = await userService.refresh();
    commit('refresh', user);
    return user;
  },

  async updateAddress({ commit, state }, address) {
    const user = await userService.updateAddress(address);
    commit('refresh', user);
    return user;
  },

  async attachPaymentMethod({ commit, state }, paymentMethod) {
    const user = await userService.attachPaymentMethod(paymentMethod);
    commit('updatePaymentMethod', user.creditCard);
    return user;
  }

};

const getters = {
  isConnected: state => {
    return _.has(state.status, 'loggedIn') && state.status.loggedIn === true;
  },
  getToken: (state, getters) => {
    if (getters.isConnected) {
      return state.user.token;
    }
    else {
      return false;
    }
  },
  getUserInfos: state => {
    return state.user.infos;
  },
  getUserEditableInfos: state => {
    const toReturn = _.pick(state.user.infos, [ 'firstName', 'lastName', 'address', 'phoneHome', 'phoneWork', 'newsletter', 'formation', 'occupation' ]);

    toReturn['phoneWorkPoste'] = '';
    if (_.has(toReturn, 'phoneWork') && toReturn.phoneWork.indexOf('#') >= 0) {
      toReturn['phoneWorkPoste'] = toReturn.phoneWork.split(' #')[1];
    }

    return toReturn;
  }
};

const mutations = {
  loginRequest(state, user) {
    state.status = { loggingIn: true };
  },
  loginSuccess(state, { jwt, user }) {
    state.status = { loggedIn: true };
    state.user = {
      infos: user,
      token: jwt
    };
  },
  refresh(state, user) {
    Vue.set(state.user, 'infos', user);
  },
  loginFailure(state) {
    state.status = {};
    state.user = null;
  },
  logout(state) {
    state.status = {};
    state.user = null;
  },
  registerSuccess(state) {
    state.status = {};
  },
  updateAccountRequest(state) {
    state.status['updatingAccount'] = true;
  },
  updateAccountSuccess(state, userUpdated) {
    Vue.set(state.user, 'infos', userUpdated);
    state.status['updatingAccount'] = false;
  },
  updateAccountFailure(state) {
    state.status['updatingAccount'] = false;
  },
  updatePaymentMethod(state, creditCard) {
    state.user.infos.creditCard = creditCard;
  }
};

export const account = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
